import React, { useState, useEffect, useCallback } from 'react';
import { Link, Outlet, useLocation, useParams, useNavigate } from 'react-router-dom';
import api, { setGuestOwnerId, clearGuestOwnerId } from '../services/api';

const GuestBusinessWorkspace = () => {
  const { ownerId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Activer le mode invité dès le montage
  useEffect(() => {
    setGuestOwnerId(ownerId);
    return () => {
      clearGuestOwnerId();
    };
  }, [ownerId]);

  // Fermer le sidebar quand on change de page (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const checkAccess = useCallback(async () => {
    try {
      // Le header X-Guest-Owner-Id est automatiquement ajouté par l'intercepteur
      const res = await api.get('/business/access');
      setAccess(res.data);
      
      if (res.data.can_access && res.data.guest_permissions?.includes('dashboard_view')) {
        loadDashboard();
      }
    } catch (err) {
      console.error(err);
      setAccess({ can_access: false });
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => {
    if (ownerId) {
      checkAccess();
    }
  }, [checkAccess, ownerId]);

  const loadDashboard = async () => {
    try {
      const res = await api.get('/business/dashboard');
      setStats(res.data.stats);
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Accès refusé
  if (!access?.can_access) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 p-8 text-center text-white">
            <span className="text-6xl mb-4 block">🚫</span>
            <h1 className="text-2xl font-bold mb-2">Accès refusé</h1>
            <p className="text-red-100">Impossible d'accéder à cet espace de travail</p>
          </div>
          <div className="p-6 text-center">
            <p className="text-gray-600 mb-6">
              {access?.error === 'NOT_TEAM_MEMBER' 
                ? "Vous n'êtes pas membre de l'équipe de cet espace de travail."
                : "L'espace de travail de ce propriétaire n'est pas accessible actuellement."
              }
            </p>
            <Link
              to="/workspace/team-invitations"
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition"
            >
              ← Retour aux workspaces partagés
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const permissions = access?.guest_permissions || [];
  const hasPermission = (perm) => permissions.includes(perm);
  const owner = access?.owner;
  const hasShopAccess = access?.has_shop_module && hasPermission('shop_view');

  const basePath = `/guest-workspace/${ownerId}`;
  const isActive = (subPath) => {
    const fullPath = subPath ? `${basePath}/${subPath}` : basePath;
    return location.pathname === fullPath || location.pathname.startsWith(fullPath + '/');
  };

  // Menu items filtrés par permissions
  const allMenuItems = [
    { subPath: '', icon: '📊', label: 'Tableau de bord', exact: true, permission: 'dashboard_view' },
    { subPath: 'products', icon: '📦', label: 'Produits & Stock', permission: 'products_view' },
    { subPath: 'restock', icon: '🚚', label: 'Ravitaillement', permission: 'restock_view' },
    { subPath: 'shop', icon: '🏪', label: 'Boutique en ligne', permission: 'shop_view', requiresShop: true },
    { subPath: 'clients', icon: '👥', label: 'Clients', permission: 'clients_view' },
    { subPath: 'invoices', icon: '📄', label: 'Factures', permission: 'invoices_view' },
    { subPath: 'quotes', icon: '📋', label: 'Devis', permission: 'quotes_view' },
    { subPath: 'payments', icon: '💰', label: 'Paiements', permission: 'payments_view' },
    { subPath: 'expenses', icon: '💸', label: 'Dépenses', permission: 'expenses_view' },
    { subPath: 'debts', icon: '📉', label: 'Dettes', permission: 'debts_view' },
    { subPath: 'prestations', icon: '🔧', label: 'Prestations', permission: 'prestations_view' },
    { subPath: 'report', icon: '📑', label: 'Bilan', permission: 'report_view' },
    { subPath: 'settings', icon: '⚙️', label: 'Paramètres', permission: 'settings_view' },
  ];

  const menuItems = allMenuItems.filter(item => {
    if (item.requiresShop && !hasShopAccess) return false;
    return hasPermission(item.permission);
  });

  // Déterminer les permissions d'écriture pour le contexte de sortie
  const canManageProducts = hasPermission('products_create') || hasPermission('products_edit');
  const canManageClients = hasPermission('clients_create') || hasPermission('clients_edit');
  const canManagePayments = hasPermission('payments_create');
  const canManageExpenses = hasPermission('expenses_create') || hasPermission('expenses_edit');
  const canManageInvoices = hasPermission('invoices_create') || hasPermission('invoices_edit');
  const canManageQuotes = hasPermission('quotes_create') || hasPermission('quotes_edit');
  const canManageDebts = hasPermission('debts_create');
  const canManagePrestations = hasPermission('prestations_create');
  const canManageRestock = hasPermission('restock_create');

  // Rôle du membre
  const roleLabel = access?.member_role_label || 'Membre';
  const roleIcon = access?.member_role ? '👤' : '⚙️';

  return (
    <div className="min-h-screen bg-gray-100 lg:flex">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white shadow-md z-50 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <h2 className="font-bold text-gray-800 text-sm truncate">
          🏢 {owner?.nom || 'Workspace'}
        </h2>
        <Link
          to="/workspace/team-invitations"
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600"
          title="Mes workspaces"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
        </Link>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed h-full z-50 bg-white shadow-lg transition-all duration-300 flex flex-col
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        w-64 lg:w-64
      `}>
        {/* Header avec info propriétaire */}
        <div className="p-4 border-b flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-lg">🏢</span>
                <h2 className="font-bold text-gray-800 truncate text-sm">
                  {owner?.nom || 'Workspace'}
                </h2>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Connecté en tant que <span className="font-semibold text-indigo-600">{roleLabel}</span>
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Link
                to="/workspace/team-invitations"
                className="p-2 hover:bg-indigo-100 rounded-lg text-indigo-600 transition-colors"
                title="Mes workspaces"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                </svg>
              </Link>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg text-gray-500"
              >
                ✕
              </button>
            </div>
          </div>
          {/* Badge mode invité */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 flex items-center gap-2">
            <span className="text-sm">👤</span>
            <span className="text-xs text-amber-700 font-medium">Mode invité</span>
          </div>
        </div>
        
        <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
          {menuItems.map(item => {
            const fullPath = item.subPath ? `${basePath}/${item.subPath}` : basePath;
            const active = item.exact 
              ? location.pathname === basePath 
              : isActive(item.subPath);
            
            return (
              <Link
                key={item.subPath || 'index'}
                to={fullPath}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition ${
                  active
                    ? 'bg-indigo-50 text-indigo-600 font-medium'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Quick Stats */}
        {stats && hasPermission('dashboard_view') && (
          <div className="p-4 border-t bg-gray-50 flex-shrink-0">
            <div className="text-xs text-gray-500 mb-2">Ce mois</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-green-100 text-green-700 rounded-lg p-2 text-center">
                <div className="font-bold">{stats.revenus?.toLocaleString()} XOF</div>
                <div>Revenus</div>
              </div>
              <div className="bg-red-100 text-red-700 rounded-lg p-2 text-center">
                <div className="font-bold">{stats.depenses?.toLocaleString()} XOF</div>
                <div>Dépenses</div>
              </div>
            </div>
          </div>
        )}
      </aside>

      {/* Main content */}
      <main className="flex-1 lg:ml-64 pt-16 lg:pt-0 transition-all duration-300">
        <Outlet context={{ 
          stats, 
          refreshStats: loadDashboard,
          // Contexte invité pour les composants enfants
          guestMode: true,
          guestOwnerId: ownerId,
          guestPermissions: permissions,
          canManage: false, // par défaut, les composants vérifient individuellement
          // Permissions détaillées par section
          canManageProducts,
          canManageClients,
          canManagePayments,
          canManageExpenses,
          canManageInvoices,
          canManageQuotes,
          canManageDebts,
          canManagePrestations,
          canManageRestock,
          // Fonction helper
          hasPermission,
        }} />
      </main>
    </div>
  );
};

export default GuestBusinessWorkspace;
